import { USER_TYPES } from '@/utils/constants';

export type RolePermissionUser = {
  userType?: number;
  isManager?: boolean;
  agentType?: string | null;
  memberType?: 'member' | 'manager' | null;
};

export type UserDepartment = 'sales' | 'marketing' | 'support';

export interface RolePermissions {
  department: UserDepartment | null;
  isPlatformSuperAdmin: boolean;
  isSystemAdmin: boolean;
  isDepartmentManager: boolean;
  isAgent: boolean;
  isCustomer: boolean;
  isGroupUser: boolean;
  isSalesManager: boolean;
  isMarketingManager: boolean;
  isSupportManager: boolean;
  isSalesUser: boolean;
  isMarketingUser: boolean;
  isSupportUser: boolean;
  isGroupManager: boolean;
  isGroupMember: boolean;
  canManageAgents: boolean;
  canManageGroups: boolean;
  canCreateGroups: boolean;
  canDeleteGroups: boolean;
  canCreateGroupMembers: boolean;
  canDeleteGroupMembers: boolean;
  canManageCrmCustomers: boolean;
  canAssignGroupTickets: boolean;
  canSelfAssignGroupTickets: boolean;
  canResolveGroupTickets: boolean;
  canTransferGroupTicketOwner: boolean;
  canTransferGroupTickets: boolean;
}

const getDepartment = (
  agentType?: string | null,
): UserDepartment | null => {
  const normalizedType = agentType?.trim().toLowerCase();

  return normalizedType === 'sales'
    || normalizedType === 'marketing'
    || normalizedType === 'support'
    ? normalizedType
    : null;
};

export const getRolePermissions = (
  user?: RolePermissionUser | null,
): RolePermissions => {
  const isDepartmentManager =
    user?.userType === USER_TYPES.SUPERVISOR && user?.isManager === true;
  const isSystemAdmin =
    user?.userType === USER_TYPES.SUPERVISOR && !isDepartmentManager;
  const department = getDepartment(user?.agentType);
  const isGroupUser = user?.userType === USER_TYPES.MEMBER;
  const groupManager = isGroupUser && user?.memberType === 'manager';
  const groupMember = isGroupUser && user?.memberType !== 'manager';
  const isSupportManager = isDepartmentManager && department === 'support';
  const canManageGroups = !isGroupUser;
  const canCreateOrDeleteGroupResources = canManageGroups && !isSupportManager;
  const canOperateGroupTickets = isSystemAdmin || isSupportManager || groupManager;

  return {
    department,
    isPlatformSuperAdmin: user?.userType === USER_TYPES.SUPER_ADMIN,
    isSystemAdmin,
    isDepartmentManager,
    isAgent: user?.userType === USER_TYPES.AGENT,
    isCustomer: user?.userType === USER_TYPES.CUSTOMER,
    isGroupUser,
    isSalesManager: isDepartmentManager && department === 'sales',
    isMarketingManager: isDepartmentManager && department === 'marketing',
    isSupportManager,
    isSalesUser: department === 'sales',
    isMarketingUser: department === 'marketing',
    isSupportUser: department === 'support',
    isGroupManager: groupManager,
    isGroupMember: groupMember,
    canManageAgents: isSystemAdmin,
    // Browse/edit access remains broader than the narrow create/delete gates.
    // Support Managers operate existing groups but cannot add or remove them.
    canManageGroups,
    canCreateGroups: canCreateOrDeleteGroupResources,
    canDeleteGroups: canCreateOrDeleteGroupResources,
    canCreateGroupMembers: canCreateOrDeleteGroupResources,
    canDeleteGroupMembers: canCreateOrDeleteGroupResources,
    canManageCrmCustomers: department !== 'marketing',
    canAssignGroupTickets: canOperateGroupTickets,
    canSelfAssignGroupTickets: groupMember,
    // Only the member who owns a ticket can resolve it back to L1. Row/detail
    // ownership checks narrow this capability to the current ticket.
    canResolveGroupTickets: groupMember,
    canTransferGroupTicketOwner: canOperateGroupTickets || groupMember,
    canTransferGroupTickets: canOperateGroupTickets || groupMember,
  };
};

const asRolePermissionUser = (user?: unknown): RolePermissionUser | undefined =>
  user && typeof user === 'object'
    ? user as RolePermissionUser
    : undefined;

export const canManageCrmCustomers = (user?: unknown): boolean =>
  getRolePermissions(asRolePermissionUser(user)).canManageCrmCustomers;

export const canManageGroups = (
  user?: RolePermissionUser | null,
): boolean => getRolePermissions(user).canManageGroups;

export const getAuthenticatedHomePath = (
  user?: RolePermissionUser | null,
): string => {
  const permissions = getRolePermissions(user);

  if (permissions.isGroupMember) return '/group-ticket';
  if (permissions.isGroupManager) return '/groups/tickets';
  return '/dashboard';
};

/** Group manager login (userType 5 with memberType "manager"). */
export const isGroupManager = (
  user?: RolePermissionUser | null,
): boolean => getRolePermissions(user).isGroupManager;

/** Plain group member login (userType 5 without the manager role). */
export const isGroupMember = (
  user?: RolePermissionUser | null,
): boolean => getRolePermissions(user).isGroupMember;

export const canManageAgents = (
  user?: RolePermissionUser | null,
): boolean => getRolePermissions(user).canManageAgents;

/**
 * Handing a queued ticket to a specific member is a manager operation. System
 * Admins reach the same queue from the Groups list and keep the capability.
 */
export const canAssignGroupTickets = (
  user?: RolePermissionUser | null,
): boolean => getRolePermissions(user).canAssignGroupTickets;

/** Only a plain member pulls a ticket out of the queue for themselves. */
export const canSelfAssignGroupTickets = (
  user?: RolePermissionUser | null,
): boolean => getRolePermissions(user).canSelfAssignGroupTickets;

/** A plain group member may resolve only tickets explicitly assigned to them. */
export const canResolveGroupTickets = (
  user?: RolePermissionUser | null,
): boolean => getRolePermissions(user).canResolveGroupTickets;

/** Ownership checks narrow this capability to the member currently assigned. */
export const canTransferGroupTicketOwner = (
  user?: RolePermissionUser | null,
): boolean => getRolePermissions(user).canTransferGroupTicketOwner;

/** System Admins, Group Managers, and the assigned plain member may move a ticket to another group. */
export const canTransferGroupTickets = (
  user?: RolePermissionUser | null,
): boolean => getRolePermissions(user).canTransferGroupTickets;
